<template>
  <RevDrawer :name="MODAL_NAMES.SELLER_INFO" :title="i18n(translations.title)">
    <template #body>
      <div class="heading-2">{{ company }}</div>
      <div v-if="logoSrc" class="p-48">
        <RevIllustration
          alt=""
          class="!h-[40px] w-auto"
          :height="40"
          :src="logoSrc"
          :width="498"
        />
      </div>
      <div class="mt-16 flex gap-8">
        <div
          v-if="seller.ratesCount.value && seller.rating.value"
          class="bg-static-info-low rounded-sm w-1/2 px-20 py-12"
        >
          <div class="caption">
            {{ i18n(translations.reviews, { count: seller.ratesCount.value }) }}
          </div>
          <div class="text-static-info-hi mt-8">
            <span class="heading-2">{{ seller.rating.value }}</span>
            <span class="caption-bold ml-4">/ 5</span>
          </div>
        </div>
        <div class="bg-static-info-low rounded-sm w-1/2 px-20 py-12">
          <div class="caption">
            {{ i18n(translations.yearsBM) }}
          </div>
          <div class="text-static-info-hi mt-12">
            <span class="heading-2">
              {{ i18n(translations.duration, { years, months }) }}
            </span>
          </div>
        </div>
      </div>
      <ul class="text-static-default-low mt-16 list-disc pl-24">
        <li>
          <span>{{ i18n(translations.companyName) }}</span>
          <span>{{ seller.company.value }}</span>
        </li>
        <li>
          <span>{{ i18n(translations.storeName) }}</span>
          <span>{{ seller.legalName.value }}</span>
        </li>
        <li v-if="seller.konbiniAddress.value">
          <span>{{ i18n(translations.address) }}</span>
          <span>{{ seller.konbiniAddress.value }}</span>
        </li>
        <li v-if="seller.konbiniEmail.value">
          <span>{{ i18n(translations.email) }}</span>
          <span>{{ seller.konbiniEmail.value }}</span>
        </li>
        <li v-if="seller.konbiniPhoneNumber.value">
          <span>{{ i18n(translations.phone) }}</span>
          <span>{{ seller.konbiniPhoneNumber.value }}</span>
        </li>
        <li v-if="seller.secondhandDealerNumber.value">
          <span>{{ i18n(translations.secondhandDealerNumber) }}</span>
          <span>{{ seller.secondhandDealerNumber.value }}</span>
        </li>
        <li v-if="seller.secondhandDealerNumberIssuingPrefecture.value">
          <span>{{ i18n(translations.publicSafetyCommission) }}</span>
          <span>{{
            seller.secondhandDealerNumberIssuingPrefecture.value
          }}</span>
        </li>
        <li v-if="seller.storeOperationManager.value">
          <span>{{ i18n(translations.storeManager) }}</span>
          <span>{{ seller.storeOperationManager.value }}</span>
        </li>
        <li v-if="seller.storeSecurityOfficer.value">
          <span>{{ i18n(translations.storeSecurityManager) }}</span>
          <span>{{ seller.storeSecurityOfficer.value }}</span>
        </li>
      </ul>
      <div class="text-static-default-low caption mt-24">
        {{ i18n(translations.disclaimer) }}
      </div>
      <RevButton
        v-if="merchantUrl"
        class="mt-24 w-full"
        :to="merchantUrl"
        variant="secondary"
      >
        {{ i18n(translations.buttonLabel) }}
      </RevButton>
    </template>
  </RevDrawer>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevDrawer } from '@ds/components/Drawer'
import { RevIllustration } from '@ds/components/Illustration'

import { MODAL_NAMES } from '~/scopes/product/pages/Product-JP/Product-JP.constants'
import { useSellerProductListPage } from '~/scopes/product-list/pages/SellerProductListPage/composables/useSellerProductListPage'

import { BIG_SELLER_LOGO } from '../../seller.constants'

import translations from './SellerInfoModal.translations'

const i18n = useI18n()

const props = defineProps<{
  merchantId?:
    | NonNullable<
        NonNullable<GetPickersResponse['selectedOffer']>['merchant']
      >['merchantId']
    | undefined
  company?:
    | NonNullable<
        NonNullable<GetPickersResponse['selectedOffer']>['merchant']
      >['company']
    | undefined
  merchantUrl?:
    | NonNullable<
        NonNullable<GetPickersResponse['selectedOffer']>['merchant']
      >['merchantUrl']
    | undefined
}>()

const seller = useSellerProductListPage(props.merchantId ?? '')

const years = computed(() => {
  return Math.floor(seller.ageInMonths.value / 12)
})
const months = computed(() => {
  return seller.ageInMonths.value % 12
})

const logoSrc = computed(() => {
  return props.company ? BIG_SELLER_LOGO[props.company] : ''
})
</script>
